// Shared helpers for downloading a file from an AUTHENTICATED backend route.
//
// Why this exists: a plain `<a href="/api/v1/...">` is a browser navigation, so
// it never carries the Authorization header (the JWT lives in localStorage, not
// in a cookie). Linking an authenticated endpoint that way returns
// {"detail":"Not authenticated"} — which is exactly what the Justification
// Report "Download" button did. The file has to be fetched through the API
// client (which attaches the token), then handed to the browser as a blob.
//
// These three functions previously lived inside DownloadAllAttachmentsButton.
// They were lifted here when a second caller (the attachment table's Download
// button in EPForm) needed the same behaviour, so the logic exists once.
import API from "../api/api";

// Pull the real file name out of a Content-Disposition header, covering both
// `filename="…"` and RFC 5987 `filename*=UTF-8''…` shapes. Falls back to the
// caller's name when the header is missing or unparsable.
export const filenameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const rfc5987 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (rfc5987?.[1]) {
    try {
      return decodeURIComponent(rfc5987[1]);
    } catch {
      /* fall through to the plain-filename branch */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] || fallback;
};

// Hand a blob to the browser as a save-to-disk action.
export const saveBlobAs = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// GET an authenticated endpoint and save the response as a file.
// `fallbackName` is used only when the response carries no Content-Disposition.
// Throws with a readable message on failure — the caller decides how to surface
// it. Errors arrive as a Blob (because responseType is set), so the JSON body is
// parsed out here rather than showing a generic failure.
export const downloadAuthedFile = async (url, fallbackName = "download") => {
  let response;
  try {
    response = await API.get(url, {
      responseType: "blob",
      // Stop the axios JSON parser touching a binary response.
      transitional: { forcedJSONParsing: false },
    });
  } catch (e) {
    let detail = e?.message || "Download failed";
    const errorBlob = e?.response?.data;
    if (errorBlob instanceof Blob) {
      try {
        const parsed = JSON.parse(await errorBlob.text());
        detail = parsed?.detail || parsed?.message || detail;
      } catch {
        /* keep the generic message */
      }
    }
    throw new Error(detail);
  }

  const filename = filenameFromDisposition(
    response.headers["content-disposition"],
    fallbackName,
  );
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  saveBlobAs(blob, filename);
  return filename;
};
